/**
 * The realistic sheet beneath the dynamic map.
 *
 * The dynamic map is an SVG camera in metres about the site centre; the sheet
 * is a raster in its own survey grid. Between them sits one affine transform:
 * sheet pixels → E/N → longitude and latitude (the fitted UTM transform) →
 * local metres → the camera's pixels. It is fitted from three points each
 * frame — the sheet is linear over an aerodrome, the fit measures that — and
 * handed to the same painter the Interactive Map uses, so the two screens show
 * the same airfield.
 */
import { useEffect, useRef, useState } from "react";
import s from "./Map.module.css";
import type { Projection } from "../../domain/geo";
import type { ViewBox } from "./AirfieldSvg";
import type { SheetTransform } from "./realBridge";

export type BaseMode = "drawing" | "day" | "night";

type Bridge = typeof import("./realBridge");
let bridgeP: Promise<Bridge> | null = null;
export const loadBridge = (): Promise<Bridge> => (bridgeP ??= import("./realBridge"));

/**
 * The matrix that takes sheet pixels to camera pixels, and the sheet's size in
 * those pixels — W and H chosen so one sheet pixel is one screen pixel, which
 * is what the painter's level-of-detail expects.
 */
export function sheetMatrix(
  vb: ViewBox,
  size: { w: number; h: number },
  T: SheetTransform,
  toXY: (E: number, N: number) => [number, number],
): { m: [number, number, number, number, number, number]; W: number; H: number } {
  const pxPerM = size.w / vb.w;
  const W = pxPerM / T.u_of_E.b;
  const H = pxPerM / -T.v_of_N.b;
  const E = (Xs: number) => (Xs / W - T.u_of_E.a) / T.u_of_E.b;
  const N = (Ys: number) => (Ys / H - T.v_of_N.a) / T.v_of_N.b;
  const screen = (Xs: number, Ys: number): [number, number] => {
    const [x, y] = toXY(E(Xs), N(Ys));
    return [((x - vb.x) / vb.w) * size.w, ((y - vb.y) / vb.h) * size.h];
  };
  const p0 = screen(0, 0);
  const p1 = screen(W, 0);
  const p2 = screen(0, H);
  return { m: [(p1[0] - p0[0]) / W, (p1[1] - p0[1]) / W, (p2[0] - p0[0]) / H, (p2[1] - p0[1]) / H, p0[0], p0[1]], W, H };
}

export function RealBase({ vb, size, mode, projection }: { vb: ViewBox; size: { w: number; h: number }; mode: "day" | "night"; projection: Projection }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const [bridge, setBridge] = useState<Bridge | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    loadBridge().then((b) => {
      if (!live) return;
      setBridge(b);
      b.loadReal()
        .then(() => live && setReady(true))
        .catch(() => {
          /* the derived pavement is a static file; without it the drawing stays */
        });
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    const c = ref.current;
    if (!c || !bridge || !ready) return;
    const T = bridge.sheet().transform;
    const toXY = (E: number, N: number): [number, number] => {
      const ll = bridge.toLonLat({ E, N });
      return projection.toXY([ll.lon, ll.lat]);
    };
    let raf = 0;
    let last = 0;
    const paint = (t: number) => {
      const g = c.getContext("2d");
      if (!g) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      if (c.width !== Math.round(size.w * dpr) || c.height !== Math.round(size.h * dpr)) {
        c.width = Math.round(size.w * dpr);
        c.height = Math.round(size.h * dpr);
      }
      g.setTransform(1, 0, 0, 1, 0, 0);
      g.clearRect(0, 0, c.width, c.height);
      const { m, W, H } = sheetMatrix(vb, size, T, toXY);
      g.setTransform(dpr * m[0], dpr * m[1], dpr * m[2], dpr * m[3], dpr * m[4], dpr * m[5]);
      bridge.paintReal(g, W, H, mode, t);
    };
    paint(performance.now());
    if (mode === "night") {
      /* the sequenced flashers run at night; eight frames a second is enough for a rabbit */
      const loop = (t: number) => {
        if (t - last > 120) {
          last = t;
          paint(t);
        }
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }
    return () => cancelAnimationFrame(raf);
  }, [bridge, ready, vb, size, mode, projection]);

  return <canvas ref={ref} className={s.base} style={{ width: size.w, height: size.h }} aria-hidden="true" />;
}
